import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, getListing, updateListing, deleteListing } from '../firebase';
import './EditListing.css';

function EditListing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    price: '',
    beds: '',
    baths: '',
    description: '',
    airbnbUrl: '',
  });
  const [newPhotos, setNewPhotos] = useState([]);
  const [error, setError] = useState('');

  const fetchListing = useCallback(async () => {
    try {
      const data = await getListing(id);
      if (!data) {
        setError('Listing not found');
        setLoading(false);
        return;
      }
      if (data.userId !== auth.currentUser.uid) {
        setError('You can only edit your own listings');
        setLoading(false);
        return;
      }
      setListing(data);
      setFormData({
        title: data.title || '',
        location: data.location || '',
        price: data.price || '',
        beds: data.beds || '',
        baths: data.baths || '',
        description: data.description || '',
        airbnbUrl: data.airbnbUrl || '',
      });
    } catch (error) {
      setError('Failed to fetch listing');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePhotoChange = (e) => {
    setNewPhotos(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.location || !formData.price) {
      setError('Please fill in required fields (title, location, price)');
      return;
    }

    try {
      await updateListing(id, auth.currentUser.uid, {
        ...formData,
        price: parseInt(formData.price),
        beds: parseInt(formData.beds) || 0,
        baths: parseInt(formData.baths) || 0,
      }, newPhotos);
      alert('Listing updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      setError(error.message || 'Failed to update listing');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteListing(id, auth.currentUser.uid);
      alert('Listing deleted successfully!');
      navigate('/dashboard');
    } catch (error) {
      setError(error.message || 'Failed to delete listing');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error && !listing) return <div className="error-container"><p className="error">{error}</p><button onClick={() => navigate('/dashboard')}>Back to Dashboard</button></div>;
  if (!listing) return <div className="loading">Listing not found</div>;

  return (
    <div className="edit-listing-container">
      <div className="edit-header">
        <button onClick={() => navigate('/dashboard')} className="back-btn">← Back</button>
        <h1>Edit Listing</h1>
      </div>

      <div className="edit-form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Basic Info</h2>
            <input
              type="text"
              name="title"
              placeholder="Listing Title"
              value={formData.title}
              onChange={handleFormChange}
              required
            />
            <input
              type="text"
              name="location"
              placeholder="City/Location"
              value={formData.location}
              onChange={handleFormChange}
              required
            />
            <input
              type="number"
              name="price"
              placeholder="Monthly Price ($)"
              value={formData.price}
              onChange={handleFormChange}
              required
            />
          </div>

          <div className="form-section">
            <h2>Property Details</h2>
            <div className="form-row">
              <input
                type="number"
                name="beds"
                placeholder="Bedrooms"
                value={formData.beds}
                onChange={handleFormChange}
              />
              <input
                type="number"
                name="baths"
                placeholder="Bathrooms"
                value={formData.baths}
                onChange={handleFormChange}
              />
            </div>
            <textarea
              name="description"
              placeholder="Description"
              value={formData.description}
              onChange={handleFormChange}
              rows="5"
            />
            <input
              type="url"
              name="airbnbUrl"
              placeholder="Airbnb Listing URL (optional)"
              value={formData.airbnbUrl}
              onChange={handleFormChange}
            />
          </div>

          <div className="form-section">
            <h2>Current Photos</h2>
            <div className="photo-gallery">
              {listing.photos && listing.photos.map((photo, index) => (
                <div key={index} className="photo-thumbnail">
                  <img src={photo} alt={`${listing.title} ${index + 1}`} />
                </div>
              ))}
            </div>

            <div className="photo-input">
              <label>Add More Photos (optional)</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoChange}
              />
              <p>{newPhotos.length} new photos to upload</p>
            </div>
          </div>

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            <button type="button" onClick={() => navigate('/dashboard')} className="cancel-btn">Cancel</button>
            <button type="button" onClick={handleDelete} className="delete-btn">Delete Listing</button>
            <button type="submit" className="save-btn">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditListing;
